import { CookieService } from '../cookies';

// event values should always be snake_case
// all custom properties within additionalParameters should be snake_case as well
// (update by RDR-868/RDR-934)
export interface DatalayerItem {
  event: string,
  value?: string | number,
  // eslint-disable-next-line camelcase
  project_name?: string,
  // eslint-disable-next-line camelcase
  component_name: string,
  additionalParameters?: Record<string, unknown>,
}

declare global {
  interface Window {
    dataLayer?: DatalayerItem[];
  }
}

type TrackingFunctions = Record<string, (element: HTMLElement) => Record<string, string>>

/*
 * Elements listed below influence the content of a tracking event's data.
 * The functions are called whenever a child of these component's "pushes" a tracking event.
 * Please refer to the following examples:
 *
 * This WILL trigger the ROCHE-HEADER matching function:
 *  <roche-header>
 *    <element-calling-trackingService.push>
 *    </element-calling-trackingService.push>
 *  </roche-header>
 *
 * This WILL NOT trigger the ROCHE-HEADER matching function:
 *  <roche-header-calling-trackingService.push>
 *  </roche-header-calling-trackingService.push>
 */
const componentAwareTrackingFunctions: TrackingFunctions = {
  'ROCHE-MOBILE-NAVIGATION': () => ({ event: 'navigation-click' }),
  'ROCHE-HEADER': () => ({ event: 'navigation-click' }),
};

const defaultItem: DatalayerItem = {
  project_name: 'roche-dot-de',
  event: undefined,
  value: undefined,
  component_name: undefined,
  additionalParameters: {},
};

export const TrackingService = {
  push(item: DatalayerItem = defaultItem): void {
    if (!CookieService.getConsent().performance) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ project_name: 'roche-dot-de', ...item });
  },
  getTrackingRelevantParents(event: Event): HTMLElement[] {
    return event
      .composedPath()
      .filter(
        (element) => Object.keys(componentAwareTrackingFunctions)
          .some(
            (componentName) => componentName === (element as HTMLElement).tagName,
          ),
      ) as HTMLElement[];
  },
  getParentAwareTrackingInfo(event): Record<string, string> {
    return this.getTrackingRelevantParents(event)
      .map((element) => componentAwareTrackingFunctions[element.tagName](element))
      .reduce((accumulator, value) => ({ ...accumulator, ...value }), {});
  },
};
